import React, { useState, useEffect, useMemo } from 'react';
import { ListChecks, Plus, Edit, Trash2, ToggleLeft, ToggleRight, Filter, Check, X } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './SettingsPage.module.css';

const fieldTypeOptions = [
  { value: 'text', label: 'Текст' },
  { value: 'number', label: 'Число' },
  { value: 'date', label: 'Дата' },
  { value: 'boolean', label: 'Булево' },
];

const getInitialProjectId = () => {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem('selected_project_id');
  if (!stored) return '';
  const parsed = Number(stored);
  return Number.isNaN(parsed) ? '' : parsed;
};

const ProjectCustomFieldList = () => {
  const [fields, setFields] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(getInitialProjectId);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    project: '',
    field_key: '',
    label: '',
    field_type: 'text',
    required: false,
    active: true,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setFields([]);
      return;
    }
    fetchFields();
  }, [selectedProject, showInactive]);

  const projectOptions = useMemo(
    () => projects.map(project => ({ value: project.id, label: project.name })),
    [projects]
  );

  const fetchProjects = async () => {
    try {
      const { data } = await api.get('/settings/projects/');
      setProjects(data);
    } catch (error) {
      toast.error('Не удалось загрузить проекты');
    }
  };

  const fetchFields = async () => {
    if (!selectedProject) return;
    try {
      setLoading(true);
      const params = { project_id: selectedProject };
      if (showInactive) params.include_inactive = true;
      const { data } = await api.get('/repairs/custom-fields/', { params });
      setFields(data);
    } catch (error) {
      toast.error('Ошибка загрузки пользовательских полей');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    if (!selectedProject) {
      toast.warn('Сначала выберите проект');
      return;
    }
    setFormData({
      project: selectedProject,
      field_key: '',
      label: '',
      field_type: 'text',
      required: false,
      active: true,
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({
      project: '',
      field_key: '',
      label: '',
      field_type: 'text',
      required: false,
      active: true,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.project || !formData.field_key.trim() || !formData.label.trim()) {
      toast.error('Заполните обязательные поля');
      return;
    }
    const payload = {
      project: formData.project,
      field_key: formData.field_key.trim(),
      label: formData.label.trim(),
      field_type: formData.field_type,
      required: formData.required,
      active: formData.active,
    };
    try {
      if (editingId) {
        await api.put(`/repairs/custom-fields/${editingId}/`, payload);
        toast.success('Поле обновлено');
      } else {
        await api.post('/repairs/custom-fields/', payload);
        toast.success('Поле создано');
      }
      closeModal();
      fetchFields();
    } catch (error) {
      toast.error('Ошибка сохранения поля');
    }
  };

  const handleEdit = (field) => {
    setFormData({
      project: field.project,
      field_key: field.field_key,
      label: field.label,
      field_type: field.field_type,
      required: field.required,
      active: field.active,
    });
    setEditingId(field.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить поле?')) return;
    try {
      await api.delete(`/repairs/custom-fields/${id}/`);
      toast.success('Поле удалено');
      fetchFields();
    } catch (error) {
      toast.error('Ошибка удаления поля');
    }
  };

  const toggleActive = async (field) => {
    try {
      await api.patch(`/repairs/custom-fields/${field.id}/`, { active: !field.active });
      toast.success(!field.active ? 'Поле включено' : 'Поле отключено');
      fetchFields();
    } catch (error) {
      toast.error('Не удалось изменить статус поля');
    }
  };

  const selectedProjectOption = projectOptions.find(option => option.value === selectedProject) || null;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <ListChecks size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Пользовательские поля
        </h3>
        <button className={styles.addButton} onClick={openAddModal} disabled={!selectedProject}>
          <Plus size={18} />
          Добавить поле
        </button>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterBlock}>
          <label>
            <Filter size={14} />
            Проект
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Выберите проект"
            value={selectedProjectOption}
            onChange={(option) => setSelectedProject(option ? option.value : '')}
            options={projectOptions}
            isClearable
          />
        </div>
        <label className={styles.toggleFilter}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />
          Показывать отключённые
        </label>
      </div>

      {!selectedProject && (
        <div className={styles.infoBanner}>
          Выберите проект, чтобы настроить поля карточек оборудования.
        </div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка полей...</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {fields.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <ListChecks size={48} />
              </div>
              <h3>Нет пользовательских полей</h3>
              <p>Добавьте поле, чтобы собирать дополнительные данные</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Ключ</th>
                  <th>Тип</th>
                  <th>Обязательное</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(field => (
                  <tr key={field.id} className={styles.clickableRow} onClick={() => handleEdit(field)}>
                    <td><strong>{field.label}</strong></td>
                    <td>
                      <code className={styles.inlineCode}>{field.field_key}</code>
                    </td>
                    <td>{fieldTypeOptions.find(option => option.value === field.field_type)?.label || field.field_type}</td>
                    <td>
                      {field.required ? (
                        <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                          <Check size={12} />
                          Обязательно
                        </span>
                      ) : (
                        <span className={`${styles.badge} ${styles.badgeMuted}`}>Опционально</span>
                      )}
                    </td>
                    <td>
                      {field.active ? (
                        <span className={`${styles.badge} ${styles.badgeSuccess}`}>Активно</span>
                      ) : (
                        <span className={`${styles.badge} ${styles.badgeDanger}`}>Выключено</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={styles.actionButton}
                        onClick={(event) => { event.stopPropagation(); toggleActive(field); }}
                        title={field.active ? 'Отключить' : 'Включить'}
                      >
                        {field.active ? <ToggleLeft size={18} /> : <ToggleRight size={18} />}
                      </button>
                      <button className={styles.actionButton} onClick={(event) => { event.stopPropagation(); handleDelete(field.id); }} title="Удалить">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <ListChecks size={20} />
                {editingId ? 'Редактировать поле' : 'Создать поле'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>Проект</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите проект"
                    value={projectOptions.find(option => option.value === formData.project) || null}
                    onChange={(option) => setFormData({ ...formData, project: option ? option.value : '' })}
                    options={projectOptions}
                    isClearable
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Ключ</label>
                  <input
                    type="text"
                    value={formData.field_key}
                    onChange={(event) => setFormData({ ...formData, field_key: event.target.value })}
                    placeholder="Например, repair_cost"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Название</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(event) => setFormData({ ...formData, label: event.target.value })}
                    placeholder="Отображаемое имя поля"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Тип поля</label>
                  <select
                    value={formData.field_type}
                    onChange={(event) => setFormData({ ...formData, field_type: event.target.value })}
                  >
                    {fieldTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.required}
                    onChange={(event) => setFormData({ ...formData, required: event.target.checked })}
                  />
                  Обязательное поле
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(event) => setFormData({ ...formData, active: event.target.checked })}
                  />
                  Активно
                </label>
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Сохранить изменения' : 'Создать поле'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCustomFieldList;
